import React, { memo, useMemo, useCallback, useEffect } from 'react'
import { Alert, Col, Row, Popover } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../../_utils/object'
import { canShowField } from '../helpers'
import { KeywordsSection } from './keywords-section'
import { PushlyAiResultsProps, SuggestedFieldsForm, VariantAction } from '../types'
import { PushlyAi } from '@pushly/aqe/lib/components'
import { VariantCard } from './variant-card'

const MAX_KEYWORDS = 20

/**
 * PushlyAiResults component
 * Handles Pushly AI variant display and selection logic including:
 * - Grid of VariantCard components
 * - Unified variant state management with enable/disable and action selection
 * - Cross-variant awareness for replace actions
 * - Batch variant operations
 */
const PushlyAiResults = memo(
    ({
        domain,
        results,
        builder,
        variantStates,
        selectedKeywords,
        setSelectedKeywords,
        onVariantEnabledChange,
        onVariantActionChange,
        onFieldSelectionsChange,
    }: PushlyAiResultsProps) => {
        const [keywordForm] = useForm<SuggestedFieldsForm>()

        // Extract keywords from the first variant (assuming all variants have similar keywords)
        const allKeywords = useMemo<string[]>(() => {
            if (!results || results.length === 0) return []
            const firstVariant = results[0]
            const keywords = (firstVariant ? extract(firstVariant, 'keywords', []) : []) as string[]
            // Normalize keywords to lowercase and sort
            const normalized = keywords.map((kw) => kw.toLowerCase())
            return normalized.sort().slice(0, MAX_KEYWORDS)
        }, [results])

        // Initialize selected keywords
        const hasKwSuggestions = allKeywords.length > 0

        // Initialize form checkbox based on whether keywords are selected
        useEffect(() => {
            if (hasKwSuggestions && selectedKeywords !== null) {
                const allSelected = selectedKeywords.length === allKeywords.length
                keywordForm.setFieldsValue({ keywords: allSelected })
            }
        }, [hasKwSuggestions, selectedKeywords, allKeywords.length, keywordForm])

        // Memoized keyword selection state
        const keywordSelectionState = useMemo(() => {
            const totalSuggestedKws = allKeywords.length
            const totalSelectedKws = selectedKeywords?.length ?? 0
            const allKwSelection = hasKwSuggestions && totalSelectedKws === totalSuggestedKws
            const partialKwSelection = hasKwSuggestions && !allKwSelection && totalSelectedKws > 0

            return {
                totalSuggestedKws,
                totalSelectedKws,
                allKwSelection,
                partialKwSelection,
            }
        }, [hasKwSuggestions, allKeywords.length, selectedKeywords?.length])

        // Update form when keyword selection state changes
        useEffect(() => {
            if (hasKwSuggestions) {
                keywordForm.setFieldsValue({
                    keywords: keywordSelectionState.allKwSelection,
                })
            }
        }, [keywordSelectionState.allKwSelection, hasKwSuggestions, keywordForm])

        // Memoized keyword change handler
        const handleKeywordChange = useCallback((kw: string, checked: boolean) => {
            setSelectedKeywords((prev) => {
                const currentSelected = prev ?? []
                return checked ? [...currentSelected, kw] : currentSelected.filter((k: string) => k !== kw)
            })
        }, [])

        // Memoized main keyword checkbox handler
        const handleMainKeywordChange = useCallback(
            ({ target: { checked } }: { target: { checked: boolean } }) => {
                const newSelection = checked ? allKeywords : []
                setSelectedKeywords(newSelection)
                keywordForm.setFieldsValue({ keywords: checked })
            },
            [allKeywords],
        )

        // Cross-variant awareness: compute per-card disabled replace actions
        // Each card should only see slots taken by OTHER enabled cards, not its own
        const disabledReplaceActionsByCard = useMemo(() => {
            const map: Record<number, number[]> = {}

            // Collect all enabled cards' slot selections
            const allSelections = Object.entries(variantStates || {})
                .filter(([, state]) => state.enabled && state.selectedAction?.type === 'replace')
                .map(([idx, state]) => ({
                    cardIndex: parseInt(idx, 10),
                    targetIndex: (state.selectedAction as { type: 'replace'; targetIndex: number }).targetIndex,
                }))

            // For each result card, disabled slots = slots taken by OTHER cards
            results.forEach((_, index) => {
                map[index] = allSelections.filter((s) => s.cardIndex !== index).map((s) => s.targetIndex)
            })

            return map
        }, [variantStates, results])

        // Smart auto-selection: when a card is enabled, auto-select the appropriate slot
        // - First enabled card: auto-select the currently active notification variant (selectedVariantIdx)
        // - Subsequent cards: auto-select "Use as new test variant" (next available slot beyond builder count)
        // Note: "Use as new test variant" is NOT exclusive — multiple cards can each create a new variant
        // Only existing variant slots are exclusive (one card per existing slot)
        const handleEnabledChange = useCallback(
            (variantIndex: number, enabled: boolean) => {
                onVariantEnabledChange(variantIndex, enabled)

                if (enabled) {
                    const builderCount = builder?.variants?.length ?? 1
                    const selectedIdx = builder?.selectedVariantIdx ?? 0
                    const maxVariants = 4

                    // Find slots already taken by other enabled cards
                    const takenSlots = Object.entries(variantStates || {})
                        .filter(
                            ([idx, state]) =>
                                parseInt(idx, 10) !== variantIndex &&
                                state.enabled &&
                                state.selectedAction?.type === 'replace',
                        )
                        .map(
                            ([, state]) =>
                                (state.selectedAction as { type: 'replace'; targetIndex: number }).targetIndex,
                        )

                    let targetSlot: number | undefined

                    // Try the currently selected notification variant first
                    if (!takenSlots.includes(selectedIdx)) {
                        targetSlot = selectedIdx
                    } else {
                        // Active slot is taken — find the next available "new" slot beyond builder count
                        // Multiple cards can each get their own new slot
                        let newSlot = builderCount
                        while (takenSlots.includes(newSlot) && newSlot < maxVariants) {
                            newSlot++
                        }
                        if (newSlot < maxVariants) {
                            targetSlot = newSlot
                        }
                        // If no new slots available, leave targetSlot undefined
                        // so the card shows "Select an action" placeholder
                    }

                    if (targetSlot !== undefined) {
                        onVariantActionChange(variantIndex, { type: 'replace', targetIndex: targetSlot })
                    }
                }
            },
            [
                onVariantEnabledChange,
                onVariantActionChange,
                variantStates,
                builder?.variants?.length,
                builder?.selectedVariantIdx,
            ],
        )

        const handleActionChange = useCallback(
            (variantIndex: number, action: VariantAction | null) => {
                onVariantActionChange(variantIndex, action)
            },
            [onVariantActionChange],
        )

        const variantCalculations = useMemo(() => {
            const builderVariantCount = builder?.variants?.length ?? 1 // At least 1 variant exists
            const maxVariants = 4

            // Calculate from new unified state
            const enabledVariants = Object.values(variantStates || {}).filter((v) => v.enabled)
            const enabledCount = enabledVariants.length

            // effectiveVariantCount = max(builderVariantCount, enabledCardCount)
            // When multiple cards are enabled, each needs its own slot.
            // If there are more enabled cards than builder variants, new slots are needed.
            const effectiveVariantCount = Math.min(Math.max(builderVariantCount, enabledCount), maxVariants)

            const hasResults = results && results.length > 0

            return {
                builderVariantCount,
                effectiveVariantCount,
                maxVariants,
                enabledCount,
                hasResults,
            }
        }, [builder?.variants?.length, variantStates, results])

        if (!variantCalculations.hasResults) {
            return (
                <div className="ai-instructions-section">
                    <div className="instructions-text">
                        <Alert
                            type="warning"
                            showIcon={true}
                            icon={<PushlyAi />}
                            message={
                                <span>
                                    We were unable to generate any AI suggestions based on the provided Landing URL.
                                    Please try again later or with a different Landing URL.
                                </span>
                            }
                        />
                    </div>
                </div>
            )
        }

        return (
            <>
                <div className="ai-instructions-section">
                    <div className="instructions-text">
                        <PushlyAi /> We've generated the following AI suggestions based on the provided Landing URL.
                        Review each one below, then enable the suggestions you'd like to use and choose where to apply
                        them.
                        {variantCalculations.builderVariantCount === 1 &&
                            ' Selecting multiple AI suggestions will enable A/B testing.'}
                    </div>
                </div>

                <div className="ai-variants-cards">
                    <Row gutter={[16, 16]}>
                        {results.map((variant, index) => {
                            const variantState = variantStates?.[index]
                            const isEnabled = variantState?.enabled ?? false
                            const selectedAction = variantState?.selectedAction ?? null

                            return (
                                <Col key={index} xs={24} sm={12} lg={12} xl={12}>
                                    <VariantCard
                                        variant={variant}
                                        variantIndex={index}
                                        domain={domain}
                                        isEnabled={isEnabled}
                                        selectedAction={selectedAction}
                                        disabledReplaceActions={disabledReplaceActionsByCard[index] ?? []}
                                        maxVariants={variantCalculations.maxVariants}
                                        currentVariantCount={variantCalculations.effectiveVariantCount}
                                        builderVariantCount={variantCalculations.builderVariantCount}
                                        onEnabledChange={handleEnabledChange}
                                        onActionChange={handleActionChange}
                                        onFieldSelectionsChange={onFieldSelectionsChange}
                                    />
                                </Col>
                            )
                        })}
                    </Row>
                </div>

                {hasKwSuggestions && canShowField(domain, 'keywords') && (
                    <KeywordsSection
                        form={keywordForm}
                        keywords={allKeywords}
                        selectedKeywords={selectedKeywords}
                        onKeywordChange={handleKeywordChange}
                        onMainCheckboxChange={handleMainKeywordChange}
                        title={
                            <>
                                <span>Keywords</span>
                                <Popover
                                    content="Selected keywords are applied to all created and replaced notifications."
                                    trigger="hover"
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </>
                        }
                        className="shared-keywords-section"
                        allSelected={keywordSelectionState.allKwSelection}
                        partialSelected={keywordSelectionState.partialKwSelection}
                    />
                )}
            </>
        )
    },
)

PushlyAiResults.displayName = 'PushlyAiResults'

export default PushlyAiResults
